/**
 * Financial Calculation Engine
 * Implements the 5 core financial metrics for business analysis
 */

/**
 * Calculate Net Profit
 * @param {number} revenue - Total revenue for the period
 * @param {number} expenses - Total expenses for the period
 * @returns {number} Net profit (revenue - expenses)
 */
export const calculateNetProfit = (revenue, expenses) => {
  const r = revenue === null || revenue === undefined ? 0 : Number(revenue)
  const e = expenses === null || expenses === undefined ? 0 : Number(expenses)
  return r - e
}

/**
 * Calculate Profit Margin Percentage
 * @param {number} netProfit - Net profit amount
 * @param {number} revenue - Total revenue
 * @returns {number} Profit margin as percentage (0-100)
 */
export const calculateProfitMargin = (revenue, expenses) => {
  const r = revenue === null || revenue === undefined ? 0 : Number(revenue)
  const e = expenses === null || expenses === undefined ? 0 : Number(expenses)
  if (r === 0) return 0
  const netProfit = r - e
  return (netProfit / r) * 100
}

/**
 * Calculate Conversion Rate Percentage
 * @param {number} customers - Number of customers acquired
 * @param {number} leads - Number of leads generated
 * @returns {number} Conversion rate as percentage (0-100)
 */
export const calculateConversionRate = (leads, customers) => {
  const l = leads === null || leads === undefined ? 0 : Number(leads)
  const c = customers === null || customers === undefined ? 0 : Number(customers)
  if (l === 0) return 0
  return (c / l) * 100
}

/**
 * Calculate Revenue Growth Percentage
 * @param {number} currentRevenue - Revenue for current period
 * @param {number} previousRevenue - Revenue for previous period
 * @returns {number} Revenue growth as percentage (can be negative)
 */
export const calculateRevenueGrowth = (currentRevenue, previousRevenue) => {
  const c = currentRevenue === null || currentRevenue === undefined ? 0 : Number(currentRevenue)
  const p = previousRevenue === null || previousRevenue === undefined ? 0 : Number(previousRevenue)
  if (p === 0) return 0
  return ((c - p) / p) * 100
}

/**
 * Calculate Expense Ratio Percentage
 * @param {number} expenses - Total expenses
 * @param {number} revenue - Total revenue
 * @returns {number} Expense ratio as percentage (0-100+)
 */
export const calculateExpenseRatio = (revenue, expenses) => {
  const r = revenue === null || revenue === undefined ? 0 : Number(revenue)
  const e = expenses === null || expenses === undefined ? 0 : Number(expenses)
  if (r === 0) return 0
  return (e / r) * 100
}

/**
 * Calculate all financial metrics at once
 * @param {Object} metrics - Current period metrics
 * @param {Object} previousMetrics - Previous period metrics for growth calculations
 * @returns {Object} All calculated financial metrics
 */
export const calculateAllMetrics = (metrics, previousMetrics = null) => {
  if (!metrics || typeof metrics !== 'object') {
    return {
      netProfit: 0,
      profitMargin: 0,
      conversionRate: 0,
      revenueGrowth: 0,
      expenseRatio: 0
    }
  }
  
  const { revenue = 0, expenses = 0, leads = 0, customers = 0 } = metrics
  const previousRevenue = previousMetrics?.revenue || 0

  const netProfit = calculateNetProfit(revenue, expenses)
  
  return {
    netProfit,
    profitMargin: calculateProfitMargin(revenue, expenses),
    conversionRate: calculateConversionRate(leads, customers),
    revenueGrowth: calculateRevenueGrowth(revenue, previousRevenue),
    expenseRatio: calculateExpenseRatio(revenue, expenses)
  }
}

/**
 * Format number as currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  const a = amount === null || amount === undefined ? 0 : Number(amount)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(a)
}

/**
 * Format number as percentage
 * @param {number} percentage - Percentage to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (percentage, decimals = 1) => {
  const p = percentage === null || percentage === undefined ? 0 : Number(percentage)
  return `${p.toFixed(decimals)}%`
}

/**
 * Validate metric input values
 * @param {Object} metrics - Metrics to validate
 * @returns {Object} Validation result with errors
 */
export const validateMetrics = (metrics) => {
  const errors = {}
  const { revenue, expenses, leads, customers, social_following } = metrics

  // Revenue validation
  if (revenue === undefined || revenue === null || revenue === '') {
    errors.revenue = 'Revenue is required'
  } else if (isNaN(revenue) || revenue < 0) {
    errors.revenue = 'Revenue must be a positive number'
  } else if (revenue > 100000000) {
    errors.revenue = 'Revenue seems unusually high (max: $100M)'
  }

  // Expenses validation
  if (expenses === undefined || expenses === null || expenses === '') {
    errors.expenses = 'Expenses is required'
  } else if (isNaN(expenses) || expenses < 0) {
    errors.expenses = 'Expenses must be a positive number'
  } else if (expenses > 100000000) {
    errors.expenses = 'Expenses seems unusually high (max: $100M)'
  }

  // Leads validation
  if (leads === undefined || leads === null || leads === '') {
    errors.leads = 'Leads is required'
  } else if (isNaN(leads) || leads < 0 || !Number.isInteger(Number(leads))) {
    errors.leads = 'Leads must be a positive whole number'
  } else if (leads > 1000000) {
    errors.leads = 'Leads seems unusually high (max: 1M)'
  }

  // Customers validation
  if (customers === undefined || customers === null || customers === '') {
    errors.customers = 'Customers is required'
  } else if (isNaN(customers) || customers < 0 || !Number.isInteger(Number(customers))) {
    errors.customers = 'Customers must be a positive whole number'
  } else if (Number(customers) > Number(leads)) {
    errors.customers = 'Customers cannot exceed leads'
  } else if (customers > 1000000) {
    errors.customers = 'Customers seems unusually high (max: 1M)'
  }

  // Social following validation (temporarily removed)
  //
  // NOTE: We are intentionally not collecting `social_following` in the UI right now
  // (both admin + end-user flows), so we should not block saves by requiring it.
  //
  // if (social_following === undefined || social_following === null || social_following === '') {
  //   errors.social_following = 'Social following is required'
  // } else if (isNaN(social_following) || social_following < 0 || !Number.isInteger(Number(social_following))) {
  //   errors.social_following = 'Social following must be a positive whole number'
  // } else if (social_following > 100000000) {
  //   errors.social_following = 'Social following seems unusually high (max: 100M)'
  // }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

/**
 * Validate financial data for health score calculation
 * @param {Object} data - Financial data to validate
 * @returns {Object} Validation result with errors and warnings
 */
export const validateFinancialData = (data) => {
  if (!data || typeof data !== 'object') {
    return {
      isValid: true,
      errors: [],
      warnings: []
    }
  }

  const errors = []
  const warnings = []
  const { revenue, expenses, leads, customers } = data

  // Check for negative values
  if (revenue < 0) errors.push('Revenue cannot be negative')
  if (expenses < 0) errors.push('Expenses cannot be negative')
  if (leads < 0) errors.push('Leads cannot be negative')
  if (customers < 0) errors.push('Customers cannot be negative')

  // Check for customers exceeding leads
  if (customers > leads && leads > 0) {
    warnings.push('Customers exceed leads count - please verify data')
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  }
}
